import type { Icon } from '@tabler/icons-react';
import {
    IconSchool,
    IconBriefcase,
    IconWorld,
    IconShare,
    IconUser,
    IconBuilding,
    IconBooks,
    IconFileDescription,
    IconFolder,
    IconMapPin,
    IconPhoto,
    IconDatabase,
    IconApps,
    IconPlayerPlay,
    IconMail,
    IconArrowsExchange,
    IconShield,
    IconCategory,
    IconKey,
    IconCamera,
    IconBadge,
    IconBus,
    IconSchema,
    IconListDetails,
    IconCreditCard,
    IconChecklist,
    IconScale,
    IconCode,
    IconSettings,
    IconHistory,
    IconAlertTriangle,
    IconId,
    IconFile,
    IconTrash,
    IconServer,
    IconSend,
    IconFileText,
    IconLock,
    IconClock
} from '@tabler/icons-react';
import { isDebugLoggingEnabled } from './debugLog';

/**
 * Centralized icon mapping for entity types (nav, tables, user info, etc.).
 * Use this everywhere so nav and list/detail icons stay in sync.
 */
const LEGITIMATE_S_ENDINGS = new Set([
    'class', 'address', 'access', 'process', 'status', 'radius',
    'dns', 'bus', 'analysis', 'series', 'diagnosis', 'synopsis',
]);

function warnIfPluralKey(key: string): void {
    if (!key || key.length < 2) return;
    if (key.endsWith('s') && !LEGITIMATE_S_ENDINGS.has(key)) {
        if (typeof window !== 'undefined' && (window as any).__hades_debug_nav_icons) {
            // eslint-disable-next-line no-console
            console.warn('[navigationIconUtils] Unexpected plural entityType key (ends with "s").', { entityType: key });
        }
    }
}

const KNOWN_ENTITY_TYPES = [
    // People
    'person',
    'user',
    'student',
    'employee',
    'contractor',
    'sponsored',
    'public',
    'shared',
    'applicant',
    'vacancy',

    // Core & Places / other domain
    'group',
    'school',
    'building',
    'class',
    'responsibilitycenter',
    'studentbusstop',
    'studenttransportationneed',
    'studenttransportneed',

    // Content
    'content',
    'file',
    'deleted',
    'photo',
    'image',
    'base',

    // Admin
    'workflow',
    'workflowactivity',
    'emailtemplate',
    'settransition',
    'policyrule',
    'policyruleaudit',
    'set',
    'functioncatalog',
    'pluginfunction',
    'featureflag',
    'requestapprovaltype',
    'approvaltype',
    'requestapproval',
    'settransitionevent',
    'spooledemail',
    'journalentry',
    'modificationerror',
    'appregistration',
    'credential',
    'scopeddomaincontroller',
    'rcjobcode',

    // Meta
    'schemaentity',
    'schemaattribute',
    'resourcetype',

    // Misc
    'apiuser',
] as const;

export type KnownEntityType = (typeof KNOWN_ENTITY_TYPES)[number];

const KNOWN_ENTITY_TYPE_SET: ReadonlySet<string> = new Set(KNOWN_ENTITY_TYPES);

type KnownEntityTypeUsage = Map<KnownEntityType, Map<string, Set<string>>>;

const knownEntityTypeUsage: KnownEntityTypeUsage = new Map();

type UnexpectedEntityTypeUsage = Map<string, Map<string, Set<string>>>;
const unexpectedEntityTypeUsage: UnexpectedEntityTypeUsage = new Map();

const usageListeners: Set<() => void> = new Set();
let notifyScheduled = false;

function scheduleUsageNotify(): void {
    if (notifyScheduled) return;
    notifyScheduled = true;
    // Avoid setState during render of other components; flush after current call stack.
    queueMicrotask(() => {
        notifyScheduled = false;
        usageListeners.forEach((l) => l());
    });
}

function getKnownEntityType(key: string): KnownEntityType | null {
    return KNOWN_ENTITY_TYPE_SET.has(key) ? (key as KnownEntityType) : null;
}

function getCallerLabel(): string {
    try {
        const stack = new Error().stack ?? '';
        const lines = stack.split('\n').map((l) => l.trim());
        for (const line of lines) {
            if (!line) continue;
            // Skip our own helpers/frames. In dev this might include file names; in prod it might be bundle locations.
            if (line.includes('navigationIconUtils.ts')) continue;
            if (line.includes('getCallerLabel')) continue;
            if (line.includes('recordKnownEntityTypeUsage')) continue;
            if (line.startsWith('Error')) continue;
            return line;
        }
        return '(unknown caller)';
    } catch {
        return '(unknown caller)';
    }
}

function recordKnownEntityTypeUsage(entityTypeKey: string, fnName: string): void {
    if (!isDebugLoggingEnabled()) return;

    const known = getKnownEntityType(entityTypeKey);
    const caller = getCallerLabel();
    if (known) {
        let perType = knownEntityTypeUsage.get(known);
        if (!perType) {
            perType = new Map();
            knownEntityTypeUsage.set(known, perType);
        }

        let callers = perType.get(fnName);
        if (!callers) {
            callers = new Set();
            perType.set(fnName, callers);
        }

        callers.add(caller);
    } else {
        let perType = unexpectedEntityTypeUsage.get(entityTypeKey);
        if (!perType) {
            perType = new Map();
            unexpectedEntityTypeUsage.set(entityTypeKey, perType);
        }

        let callers = perType.get(fnName);
        if (!callers) {
            callers = new Set();
            perType.set(fnName, callers);
        }

        callers.add(caller);
    }

    scheduleUsageNotify();
}

export function resetKnownEntityTypeUsage(): void {
    knownEntityTypeUsage.clear();
    unexpectedEntityTypeUsage.clear();
    scheduleUsageNotify();
}

export function getKnownEntityTypeUsageSnapshot(): Record<string, Record<string, string[]>> {
    const result: Record<string, Record<string, string[]>> = {};
    for (const [entityType, perFn] of knownEntityTypeUsage.entries()) {
        const fnObj: Record<string, string[]> = {};
        for (const [fnName, callers] of perFn.entries()) {
            fnObj[fnName] = Array.from(callers).sort();
        }
        result[entityType] = fnObj;
    }
    return result;
}

export function getKnownEntityTypeUsageCounts(): Record<string, { totalCallers: number; perFn: Record<string, number> }> {
    const result: Record<string, { totalCallers: number; perFn: Record<string, number> }> = {};
    for (const [entityType, perFn] of knownEntityTypeUsage.entries()) {
        const all = new Set<string>();
        const perFnCounts: Record<string, number> = {};
        for (const [fnName, callers] of perFn.entries()) {
            perFnCounts[fnName] = callers.size;
            for (const c of callers) all.add(c);
        }
        result[entityType] = { totalCallers: all.size, perFn: perFnCounts };
    }
    return result;
}

export function getUnexpectedEntityTypeUsageSnapshot(): Record<string, Record<string, string[]>> {
    const result: Record<string, Record<string, string[]>> = {};
    for (const [entityType, perFn] of unexpectedEntityTypeUsage.entries()) {
        const fnObj: Record<string, string[]> = {};
        for (const [fnName, callers] of perFn.entries()) {
            fnObj[fnName] = Array.from(callers).sort();
        }
        result[entityType] = fnObj;
    }
    return result;
}

export function getUnexpectedEntityTypeUsageCounts(): Record<string, { totalCallers: number; perFn: Record<string, number> }> {
    const result: Record<string, { totalCallers: number; perFn: Record<string, number> }> = {};
    for (const [entityType, perFn] of unexpectedEntityTypeUsage.entries()) {
        const all = new Set<string>();
        const perFnCounts: Record<string, number> = {};
        for (const [fnName, callers] of perFn.entries()) {
            perFnCounts[fnName] = callers.size;
            for (const c of callers) all.add(c);
        }
        result[entityType] = { totalCallers: all.size, perFn: perFnCounts };
    }
    return result;
}

export function subscribeEntityTypeUsage(listener: () => void): () => void {
    usageListeners.add(listener);
    return () => usageListeners.delete(listener);
}

export const getEntityIcon = (entityType: string): Icon => {
    // Be defensive: some callers pass icon keys / route fragments.
    const key = (entityType ?? '').toString().trim().toLowerCase();
    warnIfPluralKey(key);
    recordKnownEntityTypeUsage(key, 'getEntityIcon');
    switch (key) {
        case 'student':
            return IconSchool;
        case 'contractor':
            return IconBriefcase;
        case 'public':
            return IconWorld;
        case 'shared':
            return IconShare;
        case 'employee':
            return IconUser;
        case 'sponsored':
            return IconCreditCard;
        case 'applicant':
            return IconBooks;
        case 'vacancy':
            return IconFileDescription;
        case 'group':
            return IconFolder;
        case 'school':
            return IconSchool;
        case 'building':
            return IconBuilding;
        case 'class':
            return IconBooks;
        case 'responsibilitycenter':
            return IconMapPin;
        case 'image':
            return IconPhoto;
        case 'base':
            return IconDatabase;
        case 'studentbusstop':
            return IconMapPin; // Using location icon for bus stop
        // Admin entity types
        case 'workflow':
            return IconSchema;
        case 'workflowactivity':
            return IconPlayerPlay;
        case 'emailtemplate':
            return IconMail;
        case 'settransition':
            return IconArrowsExchange;
        case 'policyrule':
            return IconShield;
        case 'policyruleaudit':
            return IconScale;
        case 'set':
            return IconCategory;
        case 'functioncatalog':
            return IconBooks;
        case 'pluginfunction':
            return IconCode;
        case 'featureflag':
            return IconSettings;
        case 'requestapprovaltype':
            return IconChecklist;
        // Other entity types
        case 'apiuser':
            return IconKey;
        case 'photo':
            return IconCamera;
        case 'rcjobcode':
            return IconBadge;
        case 'studenttransportationneed':
            return IconBus;
        case 'studenttransportneed':
            return IconBus;
        case 'credential':
            return IconLock;
        case 'appregistration':
            return IconId;
        case 'journalentry':
            return IconHistory;
        case 'modificationerror':
            return IconAlertTriangle;
        case 'deleted':
            return IconTrash;
        case 'file':
            return IconFile;
        case 'content':
            return IconFileText;
        case 'requestapproval':
            return IconClock;
        case 'settransitionevent':
            return IconHistory;
        case 'spooledemail':
            return IconSend;
        case 'scopeddomaincontroller':
            return IconServer;
        // Meta entity types
        case 'schemaentity':
            return IconSchema;
        case 'schemaattribute':
            return IconListDetails;
        case 'resourcetype':
            return IconListDetails;
        case 'person':
        case 'user':
            return IconUser;
        default:
            return IconApps;
    }
};

/**
 * Color mapping for entity types (must match getEntityIcon usage).
 */
export const getEntityColor = (entityType: string): string => {
    const key = (entityType ?? '').toString().trim().toLowerCase();
    warnIfPluralKey(key);
    recordKnownEntityTypeUsage(key, 'getEntityColor');
    switch (key) {
        case 'student':
            return '#1976d2';
        case 'contractor':
            return '#388e3c';
        case 'public':
            return '#f57c00';
        case 'shared':
            return '#7b1fa2';
        case 'employee':
            return '#2e7d32';
        case 'sponsored':
            return '#6a1b9a'; // Purple for sponsored (distinct from public orange)
        case 'applicant':
            return '#d32f2f';
        case 'vacancy':
            return '#9c27b0';
        case 'group':
            return '#7b1fa2';
        case 'school':
            return '#1976d2';
        case 'building':
            return '#388e3c';
        case 'class':
            return '#f57c00';
        case 'responsibilitycenter':
            return '#1565c0';
        case 'image':
            return '#9c27b0';
        case 'base':
            return '#2e7d32';
        case 'studentbusstop':
            return '#ff9800'; // Orange color for bus stop
        // Admin entity types
        case 'workflow':
            return '#9c27b0'; // Purple for workflows
        case 'workflowactivity':
            return '#7b1fa2'; // Darker purple for workflow activities
        case 'emailtemplate':
            return '#1976d2'; // Blue for email templates
        case 'settransition':
            return '#f57c00'; // Orange for transitions
        case 'policyrule':
            return '#d32f2f'; // Red for policy rules
        case 'set':
            return '#388e3c'; // Green for sets
        // Other entity types
        case 'apiuser':
            return '#7b1fa2'; // Purple for API users
        case 'photo':
            return '#9c27b0'; // Purple for photos
        case 'rcjobcode':
            return '#1565c0'; // Blue for RC job codes
        case 'studenttransportationneed':
            return '#ff9800'; // Orange for student transport needs
        case 'studenttransportneed':
            return '#ff9800';
        case 'credential':
            return '#7b1fa2';
        case 'appregistration':
            return '#1565c0';
        case 'journalentry':
            return '#455a64';
        case 'modificationerror':
            return '#d32f2f';
        case 'deleted':
            return '#616161';
        case 'file':
        case 'content':
            return '#5d4037';
        case 'requestapproval':
            return '#00acc1'; // Teal to clearly differ from requestapprovaltype blue
        case 'settransitionevent':
            return '#fb8c00'; // Variant of settransition (closely related)
        case 'spooledemail':
            return '#1976d2';
        case 'scopeddomaincontroller':
            return '#2e7d32';
        // Meta entity types
        case 'schemaentity':
            return '#455a64'; // Blue-grey for schema entities
        case 'schemaattribute':
            return '#607d8b'; // Lighter blue-grey for schema attributes
        case 'resourcetype':
            return '#607d8b';
        default:
            // Fallback: check if entity type contains "transport" (case-insensitive)
            if (entityType.toLowerCase().includes('transport')) {
                return '#ff9800'; // Orange for student transport needs
            }
            return '#1976d2';
    }
};

/**
 * Returns the icon name (string) for entity type, for use by services that need a string (e.g. EntityInfo).
 * Kept in sync with getEntityIcon so nav and list/detail stay consistent.
 */
export const getEntityIconName = (entityType: string): string => {
    const key = (entityType ?? '').toString().trim().toLowerCase();
    warnIfPluralKey(key);
    recordKnownEntityTypeUsage(key, 'getEntityIconName');
    switch (key) {
        case 'student': return 'IconSchool';
        case 'contractor': return 'IconBriefcase';
        case 'public': return 'IconWorld';
        case 'shared': return 'IconShare';
        case 'employee': return 'IconUser';
        case 'sponsored': return 'IconCreditCard';
        case 'applicant': return 'IconBooks';
        case 'vacancy': return 'IconFileDescription';
        case 'group': return 'IconFolder';
        case 'school': return 'IconSchool';
        case 'building': return 'IconBuilding';
        case 'class': return 'IconBooks';
        case 'responsibilitycenter': return 'IconMapPin';
        case 'image': return 'IconPhoto';
        case 'base': return 'IconDatabase';
        case 'studentbusstop': return 'IconMapPin';
        case 'photo':
            return 'IconCamera';
        case 'workflow': return 'IconSchema';
        case 'workflowactivity': return 'IconPlayerPlay';
        case 'emailtemplate': return 'IconMail';
        case 'settransition': return 'IconArrowsExchange';
        case 'policyrule': return 'IconShield';
        case 'policyruleaudit': return 'IconScale';
        case 'set': return 'IconCategory';
        case 'functioncatalog': return 'IconBooks';
        case 'pluginfunction': return 'IconCode';
        case 'featureflag': return 'IconSettings';
        case 'requestapprovaltype':
            return 'IconChecklist';
        case 'approvaltype':
            return 'IconChecklist';
        case 'requestapproval': return 'IconClock';
        case 'settransitionevent': return 'IconHistory';
        case 'spooledemail': return 'IconSend';
        case 'journalentry': return 'IconHistory';
        case 'modificationerror': return 'IconAlertTriangle';
        case 'appregistration': return 'IconId';
        case 'credential': return 'IconLock';
        case 'scopeddomaincontroller': return 'IconServer';
        case 'deleted': return 'IconTrash';
        case 'file': return 'IconFile';
        case 'content': return 'IconFileText';
        case 'studenttransportneed': return 'IconBus';
        case 'schemaentity': return 'IconSchema';
        case 'schemaattribute': return 'IconListDetails';
        case 'resourcetype': return 'IconListDetails';
        case 'person':
        case 'user': return 'IconUser';
        default: return key ? 'IconApps' : 'IconUser';
    }
};

/**
 * Returns both icon component and color for an entity type (convenience for avatars, etc.).
 * Normalizes entity type (case-insensitive). Use this so nav and list/detail stay in sync.
 */
export const getEntityIconAndColor = (entityType: string): { Icon: Icon; color: string } => {
    const key = (entityType || '').trim().toLowerCase();
    warnIfPluralKey(key);
    recordKnownEntityTypeUsage(key, 'getEntityIconAndColor');
    return {
        Icon: getEntityIcon(key || 'person'),
        color: getEntityColor(key || 'person')
    };
};

/**
 * Emoji mapping for entity types (for text-based displays)
 */
export const getEntityEmoji = (entityType: string): string => {
    switch (entityType.toLowerCase()) {
        case 'class':
            return '📚';
        case 'school':
            return '🏫';
        case 'building':
            return '🏢';
        case 'responsibilitycenter':
            return '🏛️';
        case 'studentbusstop':
            return '🚌';
        case 'group':
            return '👥';
        case 'employee':
            return '👨‍💼';
        case 'contractor':
            return '👷';
        case 'applicant':
            return '📝';
        case 'sponsored':
            return '🎫';
        case 'public':
            return '🌐';
        case 'shared':
            return '🤝';
        case 'user':
        case 'person':
        case 'student':
            return '👤';
        case 'workflow':
            return '⚙️';
        case 'workflowactivity':
            return '▶️';
        case 'emailtemplate':
            return '📧';
        case 'settransition':
            return '↔️';
        case 'policyrule':
            return '📋';
        case 'set':
            return '📁';
        case 'photo':
            return '📷';
        case 'vacancy':
            return '💼';
        case 'apiuser':
            return '🔑';
        case 'schemaentity':
            return '🗂️';
        case 'schemaattribute':
            return '📝';
        default:
            return '📄';
    }
};
